import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { mensagemDeErroLogin } from '../../../../shared/utils/mensagem-erro-login';
import { NormalizeService } from '../../../../shared/utils/normalize.service';
import { ServicoCarta } from '../../models/servico-detalhe.model';
import { aceitaSolicitacao } from '../../models/solicitacao-servico.model';

/** O que o card de acesso oferece no lugar do botão anônimo, e por quê. */
type AcessoAnonimo = 'carregando' | 'permitido' | 'informativo' | 'identificacao' | 'indisponivel';

/**
 * Um acordeão da tela.
 *
 * O `conteudo` é **HTML** quando vem da API (`html: true`) e texto puro nas duas seções
 * institucionais — daí a marca, que decide no template entre `[innerHTML]` e `{{ }}`.
 */
interface SecaoServico {
  id: string;
  titulo: string;
  icone: string;
  conteudo: string;
  html: boolean;
}

/**
 * As duas seções que **não têm origem no banco**.
 *
 * O catálogo legal da carta (`BlocoServico.PerguntasFixas`, no back-end) tem SETE
 * perguntas, e nenhuma é "Manifestação sobre o serviço" ou "Observações". Criá-las lá
 * mudaria o que o adm é obrigado a preencher em todo serviço já cadastrado e exigiria
 * backfill — é change do módulo administrativo, não desta tela.
 *
 * Enquanto isso, as duas são informação **transversal do município**, verdadeira para
 * qualquer serviço: a Ouvidoria recebe manifestação sobre todos, e a ressalva de
 * atualização vale para toda página. Ficam aqui, sempre ao fim, depois do conteúdo que o
 * órgão de fato escreveu.
 *
 * ⚠️ Se um órgão cadastrar um bloco personalizado com um destes títulos, a tela mostrará
 * duas seções parecidas. É o custo aceito desta decisão.
 */
const SECOES_INSTITUCIONAIS: readonly SecaoServico[] = [
  {
    id: 'institucional-manifestacao',
    titulo: 'Manifestação sobre o serviço',
    icone: 'headphones',
    conteudo:
      'Elogios, sugestões, reclamações e denúncias podem ser registrados pela Ouvidoria Municipal a qualquer momento.',
    html: false,
  },
  {
    id: 'institucional-observacoes',
    titulo: 'Observações',
    icone: 'info',
    conteudo:
      'As informações desta página podem ser atualizadas pelo órgão responsável a qualquer momento.',
    html: false,
  },
];

@Component({
  selector: 'app-etapa-informacoes',
  templateUrl: './etapa-informacoes.component.html',
  styleUrls: ['./etapa-informacoes.component.css'],
  imports: [FormField, LucideModule, NgOptimizedImage, NgxMaskDirective],
  // Mantém o valor mascarado no modelo; o envio normaliza os dígitos antes de chamar a API.
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class EtapaInformacoesComponent {
  private readonly authService = inject(AuthService);
  private readonly normalizeService = inject(NormalizeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly descricao = input('');

  /**
   * A carta do serviço vinda da API. `null` enquanto carrega, quando falha, ou nas rotas
   * dos catálogos estáticos, que não têm serviço publicado por trás.
   */
  readonly carta = input<ServicoCarta | null>(null);

  readonly cartaCarregando = input(false);

  readonly cartaFalhou = input(false);

  /** "Tentar novamente" do aviso de falha. */
  readonly recarregarCarta = output<void>();

  /**
   * Indica que o login foi feito pelo formulário desta etapa. Fica no componente pai porque a etapa
   * é recriada a cada troca de passo; com sessão ativa vinda de outra tela continua `false`, de modo
   * que as opções iniciais (login, gov.br e anônimo) seguem disponíveis aqui.
   */
  readonly sessaoIniciada = input(false);

  /** Cada incremento vindo do pai rola até o card de acesso e foca o campo de documento. */
  readonly focarLogin = input(0);

  readonly autenticado = output<void>();

  readonly saiu = output<void>();

  /** Acesso pelo gov.br — ainda sem integração, apenas libera a etapa de solicitação. */
  readonly acessarGov = output<void>();

  readonly solicitar = output<void>();

  /** Seguir sem se identificar; separado de `solicitar` porque muda o que a etapa pede. */
  readonly seguirAnonimo = output<void>();

  private readonly usuario = toSignal(this.authService.currentUser, {
    initialValue: this.authService.currentUserValue,
  });

  readonly logado = computed(() => this.sessaoIniciada() && !!this.usuario());

  readonly nomeUsuario = computed(() => this.usuario()?.nome?.trim() || 'cidadão');

  /** Máscara nativa do ngx-mask: alterna entre CPF e CNPJ conforme os dígitos digitados. */
  readonly mascaraDocumento = 'CPF_CNPJ';

  readonly dados = signal({ documento: '', senha: '' });
  readonly loginForm = form(this.dados);

  readonly senhaVisivel = signal(false);
  readonly entrando = signal(false);
  readonly erroLogin = signal('');
  readonly tentouEntrar = signal(false);

  readonly documentoValido = computed(() => {
    const digitos = this.dados().documento.replace(/\D/g, '');
    return digitos.length === 11 || digitos.length === 14;
  });

  readonly senhaValida = computed(() => this.dados().senha.trim().length > 0);

  readonly loginValido = computed(() => this.documentoValido() && this.senhaValida());

  /**
   * Se "Solicitar anonimamente" aparece — decidido pela carta JÁ carregada, sem chamada nova: o
   * serviço precisa aceitar solicitação ('Solicitação Online' ou 'Agendamento'), permitir anônimo
   * e não exigir gov.br. Rota estática, sem carta, não tem solicitação on-line.
   *
   * Só o botão anônimo é governado aqui: o formulário de login e o gov.br ficam como estão.
   */
  readonly acessoAnonimo = computed<AcessoAnonimo>(() => {
    if (this.cartaCarregando()) return 'carregando';

    const carta = this.carta();

    if (!carta) return 'indisponivel';
    if (!aceitaSolicitacao(carta.tiposAtendimento)) return 'informativo';
    if (!carta.permiteAnonimo || carta.exigeGovBr) return 'identificacao';

    return 'permitido';
  });

  /** Datas de publicação da carta; vazias enquanto ela não chegou. */
  readonly criadoEm = computed(() => this.carta()?.publicadoEm ?? '');

  /** Vazia quando o serviço nunca foi editado — e então a linha nem aparece. */
  readonly atualizadoEm = computed(() => this.carta()?.atualizadoEm ?? '');

  /**
   * Os acordeões: primeiro o que o órgão escreveu, na ordem que ele definiu, depois as duas
   * seções institucionais.
   *
   * O conteúdo dos blocos da API é HTML do editor do adm (`html: true`); o das
   * institucionais é texto puro. Nenhum acordeão é inventado para um bloco que o órgão não
   * preencheu: o back-end já omite bloco desabilitado ou vazio, e a tela não os repõe.
   */
  readonly secoes = computed<readonly SecaoServico[]>(() => {
    const blocos = (this.carta()?.blocos ?? []).map<SecaoServico>((bloco) => ({
      id: bloco.id,
      titulo: bloco.titulo,
      icone: bloco.icone,
      conteudo: bloco.conteudo,
      html: true,
    }));

    return [...blocos, ...SECOES_INSTITUCIONAIS];
  });

  /** Os órgãos responsáveis, já em texto pronto; vazio quando não há vínculo. */
  readonly orgaos = computed(() => this.carta()?.orgaos ?? []);

  /**
   * Sem nenhum bloco da API: o serviço está publicado mas a carta não tem conteúdo, ou a
   * rota é de um catálogo estático. A tela avisa, em vez de exibir só as duas seções
   * institucionais como se fossem a carta.
   */
  readonly cartaVazia = computed(
    () => !this.cartaCarregando() && !this.cartaFalhou() && (this.carta()?.blocos.length ?? 0) === 0
  );

  readonly secaoAberta = signal<string>('');

  private readonly cartaoLogin = viewChild<ElementRef<HTMLElement>>('cartaoLogin');
  private readonly campoDocumento = viewChild<ElementRef<HTMLInputElement>>('campoDocumento');

  constructor() {
    // O primeiro acordeão abre sozinho quando a carta chega — antes era o id fixo
    // 'o-que-e', que não existe mais: os ids agora vêm da ordem dos blocos do órgão.
    effect(() => {
      const primeira = this.secoes()[0];

      if (primeira && this.secaoAberta() === '') {
        this.secaoAberta.set(primeira.id);
      }
    });

    effect(() => {
      if (this.focarLogin() === 0) return;

      const campo = this.campoDocumento()?.nativeElement;
      const cartao = this.cartaoLogin()?.nativeElement;

      (campo ?? cartao)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo?.focus({ preventScroll: true });
    });
  }

  alternarSecao(id: string): void {
    this.secaoAberta.update((aberta) => (aberta === id ? '' : id));
  }

  alternarSenha(): void {
    this.senhaVisivel.update((visivel) => !visivel);
  }

  entrar(evento: Event): void {
    evento.preventDefault();

    this.tentouEntrar.set(true);
    this.erroLogin.set('');

    if (!this.loginValido() || this.entrando()) {
      return;
    }

    const { documento, senha } = this.dados();
    const usuario = this.normalizeService.cpfCnpj(documento);

    this.entrando.set(true);

    this.authService
      .login(usuario, senha)
      .pipe(
        finalize(() => this.entrando.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.dados.update((atual) => ({ ...atual, senha: '' }));
          this.tentouEntrar.set(false);
          this.autenticado.emit();
        },
        error: (erro) => this.erroLogin.set(mensagemDeErroLogin(erro)),
      });
  }

  sair(): void {
    this.authService.logout();
    this.dados.set({ documento: '', senha: '' });
    this.tentouEntrar.set(false);
    this.erroLogin.set('');
    this.senhaVisivel.set(false);
    this.saiu.emit();
  }
}
